import type { TFunction } from 'i18next';
import type { OrgMember, OrgRole } from '@examing/types';
import type { StatusTone } from 'components/ui/status-badge';
import { formatRelativeTime } from 'lib/relativeTime';

/** Roles a member can be given from this page (owners and super admins are not assignable). */
export const ASSIGNABLE_ROLES: { value: OrgRole; labelKey: string }[] = [
  { value: 'ORG_ADMIN', labelKey: 'role_admin' },
  { value: 'AUTHOR', labelKey: 'role_author' },
  { value: 'PROCTOR', labelKey: 'role_proctor' },
  { value: 'NO_ACCESS', labelKey: 'role_no_access' },
];

/** Roles read as plain words; colour is kept for status, which is what needs attention. */
const ROLE_LABEL_KEYS: Record<string, string> = {
  SUPER_ADMIN: 'role_super_admin',
  ORG_ADMIN: 'role_admin',
  AUTHOR: 'role_author',
  PROCTOR: 'role_proctor',
  CANDIDATE: 'role_candidate',
  NO_ACCESS: 'role_no_access',
};

export const MEMBER_STATUSES = ['ACTIVE', 'PENDING', 'SUSPENDED'] as const;
export const STATUS_TONE: Record<string, StatusTone> = { ACTIVE: 'success', PENDING: 'warning', SUSPENDED: 'error' };
export const STATUS_LABEL_KEYS: Record<string, string> = { ACTIVE: 'active', PENDING: 'pending', SUSPENDED: 'suspended' };

export function roleLabel(role: string, t: TFunction): string {
  return ROLE_LABEL_KEYS[role] ? t(ROLE_LABEL_KEYS[role]) : role;
}

export function memberStatus(member: OrgMember): string {
  return member.user?.status ?? 'ACTIVE';
}

export function statusLabel(status: string, t: TFunction): string {
  return STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : status;
}

/** "Hozir", "5 daqiqa oldin", "Kecha" — sentence case, since it starts a cell or a meta line; "never" when unseen. */
export function lastActiveLabel(member: OrgMember, t: TFunction, language: string): string {
  const relative = member.lastActiveAt ? formatRelativeTime(member.lastActiveAt, language) : '';
  if (!relative) return t('never');
  return relative.charAt(0).toLocaleUpperCase(language) + relative.slice(1);
}

/** What a member row calls on the page that owns the dialogs and mutations. */
export interface MemberActions {
  onView: (member: OrgMember) => void;
  onChangeRole: (member: OrgMember, role: OrgRole) => void;
  onToggleSuspend: (member: OrgMember) => void;
  onRemove: (member: OrgMember) => void;
}
